'use client';

import { useEffect, useId, useRef, useState } from 'react';
import {
  CASE_EVENT,
  CHANNEL_LADDER,
  CHANNEL_THREAD_STATUS,
  DOMAIN_CASE_STATUS,
  DOMAIN_TRUST,
  PHOTO_MATCH,
  PhotoMatchState,
  type EvidenceItem,
} from '@content-removal/shared';
import { ExternalLink, Tooltip } from './primitives';
import { Chip } from './vocab';
// Aliased: the component below is also called Liveness, which is the name a
// caller wants at a JSX site.
import { Liveness as LivenessState, shownLiveness, type CheckState } from '@content-removal/shared';

/* ── status vocabulary ─────────────────────────────────────────────
   Both tables moved to `packages/shared/src/vocabulary.ts`. They were the
   only two that had ever been written down once, and keeping them here meant
   every panel screen with a status column of its own could not reach them
   without importing React: which is why nine of those screens ended up with
   a private copy instead. The words and the tone now live beside every other
   vocabulary in this product, and `Chip` is the one thing that turns a tone
   into a class. */

/** A domain case's own status — pending/in_review/hold/in_progress/done/… */
export function CaseStatusChip({ status }: { status: string }) {
  return <Chip vocabulary={DOMAIN_CASE_STATUS} code={status} />;
}

/** One channel thread's status — sent/waiting/new_reply/declined/done/… */
export function ChannelStatusChip({ status, later = false }: { status: string; later?: boolean }) {
  /* A rung whose turn has not come says so, rather than borrowing "Drafted"
     from the channel being worked: nothing is written on it yet. */
  if (later) return <span className="chip">{'Not started'}</span>;
  return <Chip vocabulary={CHANNEL_THREAD_STATUS} code={status} />;
}

/**
 * What kind of site a domain is, and whether her photos back the case up.
 *
 * Two tags rather than one score, because they answer different questions and
 * a support member weighs them differently: a pirate site with no photo on
 * file is still obvious work, while a legitimate host with a confirmed match
 * is the one to read twice. Deliberately never shown for a whitelisted
 * domain — one of those never reaches a queue at all.
 */
export function DomainTrustChip({ level }: { level: string }) {
  return <Chip vocabulary={DOMAIN_TRUST} code={level} />;
}

/**
 * Only worth drawing when it says something. "No photo to compare" is the
 * state of most findings until clients upload their references, and a tag on
 * every single row that reads the same is furniture, not information.
 */
export function PhotoMatchChip({ state }: { state: string }) {
  if (state === PhotoMatchState.NO_PHOTO) return null;
  return <Chip vocabulary={PHOTO_MATCH} code={state} />;
}

/** @deprecated use {@link CaseStatusChip}. Kept as an alias during the
 * domain-case rollout so a stray import doesn't hard-fail the build. */
export const StatusChip = CaseStatusChip;

/**
 * `DomainCaseEvent.type` is a different vocabulary from a case's own status:
 * an audit-log verb rather than a state a case sits in. See `CASE_EVENT` in
 * `@content-removal/shared` for why the two cannot share one table.
 */
export function EventChip({ type }: { type: string }) {
  return <Chip vocabulary={CASE_EVENT} code={type} />;
}

/**
 * Whether the page is still there — the question every other column is a proxy
 * for. A case status says what we have done, and the two come apart exactly when
 * it matters: a notice sent three weeks ago over a page that went down last
 * Tuesday. `blocked` is its own state, not a kind of unknown. Deliberately
 * quiet: `gone` gets the green, and red on `live` would leave the table shouting
 * at her continuously.
 */
export function Liveness({
  liveness,
  checkState,
  checkedAt,
}: {
  liveness: LivenessState;
  checkState: CheckState;
  checkedAt?: string | null;
}) {
  const state = shownLiveness({ liveness, checkState });
  if (state === LivenessState.UNKNOWN) {
    return (
      <span className="pending-mark">
        <Tooltip label="Not checked yet">
          <PendingIcon />
        </Tooltip>
      </span>
    );
  }

  const when = checkedAt ? `Checked ${new Date(checkedAt).toLocaleDateString()}` : undefined;

  if (state === LivenessState.BLOCKED) {
    return (
      <span className="chip chip-attention" title={when}>
        Site blocked our check
      </span>
    );
  }

  if (state === LivenessState.GONE) {
    return (
      <span className="chip chip-resolved" title={when}>
        Page is gone
      </span>
    );
  }

  return (
    <span className="chip" title={when}>
      Still online
    </span>
  );
}

/**
 * The five-plus-one channels a domain case can send to, each with its own
 * independent status — not a ladder climbed in order any more. `dmca` reads
 * as locked (`rail-rung-passed`'s old muted ink, repurposed) while
 * `dmcaGateOpen` is false, since it is the one channel that stays gated on
 * every other one having ended negatively.
 */
export function ChannelLadder({
  threads,
  dmcaGateOpen,
}: {
  threads: ReadonlyArray<{ channel: string; status: string }>;
  /** Whether DMCA may be sent yet; irrelevant to every other channel. */
  dmcaGateOpen?: boolean;
}) {
  const byChannel = new Map(threads.map((t) => [t.channel, t.status]));

  return (
    <span className="rail-ladder">
      {CHANNEL_LADDER.map((rung, index) => {
        const status = byChannel.get(rung.key) ?? 'not_applicable';
        const locked = rung.key === 'dmca' && !dmcaGateOpen && status === 'not_applicable';
        return (
          <span key={rung.key} className="rail-ladder">
            <span
              className={['mono', 'rail-rung', locked ? 'rail-rung-locked' : ''].filter(Boolean).join(' ')}
              title={locked ? 'DMCA opens once every other channel has ended negatively' : undefined}
            >
              {rung.label}
            </span>
            {index < CHANNEL_LADDER.length - 1 && (
              <span aria-hidden className="rail-rung-sep">
                ›
              </span>
            )}
          </span>
        );
      })}
    </span>
  );
}

/**
 * How strong a match is, and on what basis: two lines, the figure over the
 * method, because on one line the number stopped being scannable down a column.
 * Not a bar — a bar answers "roughly how much", and 99 and 72 mean specific
 * different things she wants to read rather than estimate.
 */
export function ConfidenceMeter({
  confidence,
  hasImageMatch,
  method,
}: {
  confidence: number | null;
  hasImageMatch: boolean;
  /**
   * What produced the match: `exact hash`, `video hash`, `perceptual`,
   * `image set`, `username + avatar`, `partial`. Absent while the pipeline
   * still reports only whether an image matched, in which case that is said
   * instead of inventing a precision the data does not have.
   */
  method?: string | null;
}) {
  if (confidence === null) {
    return (
      <span className="pending-mark">
        <Tooltip label="Not checked yet">
          <PendingIcon />
        </Tooltip>
      </span>
    );
  }

  const percent = Math.round(confidence * 100);
  const basis = matchBasis(method, hasImageMatch);

  /*
    Heights in pixels and not ratios in `.confidence-*`: a ratio resolves against
    inherited font-size, and beside a `.mono` rule the boxes came out shorter
    than their glyphs and the second line printed over the first. No monospace
    face either — `tabular-nums` lines the column up alone, and the slashed zero
    made "0%" look like two collided lines.
  */
  return (
    <span className="confidence">
      <span className="confidence-figure">{percent}%</span>
      <span className="confidence-basis">{basis}</span>
    </span>
  );
}

/* How the match was made, in words the owner of the photo can act on: the
   difference between "this is your file" and "this looks like your photo,
   cropped or recoloured" decides whether a notice is worth sending. */
function matchBasis(method: string | null | undefined, hasImageMatch: boolean): string {
  if (method === 'phash') return 'exact image';
  if (method === 'clip') return 'looks like yours';
  return hasImageMatch ? 'image match' : 'page text';
}

/**
 * Protected view; the signature element of this product.
 *
 * We never keep the original frame of a pirate page: it is the leaked material
 * itself, and holding it would make us the thing we exist to remove. The blur
 * is the policy made visible, and the hash proves the blurred copy came from
 * the page we say it did.
 */
export function EvidenceTile({ item }: { item: EvidenceItem }) {
  const [explaining, setExplaining] = useState(false);

  return (
    <figure className="evidence">
      <div className="evidence-frame">
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          src={item.screenshotUrl}
          alt="Blurred capture of the page hosting the content"
          className="evidence-shot"
        />
        <span className="chip evidence-badge">Protected view</span>
      </div>

      <figcaption className="evidence-caption">
        <div className="evidence-head">
          <span className="evidence-when">
            Captured {new Date(item.capturedAt).toLocaleString()}
          </span>
          <ExternalLink href={item.htmlUrl} className="evidence-link">
            Page text
          </ExternalLink>
        </div>

        <div className="mono evidence-hash">sha256 {item.sha256.slice(0, 24)}…</div>

        <button
          type="button"
          className="linkbtn evidence-why"
          onClick={() => setExplaining((open) => !open)}
          aria-expanded={explaining}
        >
          {explaining ? 'Hide' : 'Why is this blurred?'}
        </button>

        {explaining && (
          <p className="evidence-explainer">
            We blur every capture the moment it is taken and never store the original. The
            hash above comes from the unblurred frame, so this is still proof of what was on
            the page, without us keeping a copy of your content.
          </p>
        )}
      </figcaption>
    </figure>
  );
}

/**
 * A sentence the page wants read, in one of four tones, and four is the limit:
 * `info` explains, `warn` says it will not do what you expect, `bad` says it is
 * broken, `ok` says what you just did worked. A fifth would mean the first four
 * had stopped being distinguishable at a glance.
 *
 * Class names, not an inline object, so a screen without React — a marketing
 * page, an email preview — gets the same box from one edit in `globals.css`.
 */
export function Notice({
  tone,
  children,
}: {
  tone: 'ok' | 'bad' | 'warn' | 'info';
  children: React.ReactNode;
}) {
  const className = { ok: 'note note-ok', bad: 'note note-bad', warn: 'note note-warn', info: 'note' }[
    tone
  ];
  return (
    // Only a refusal interrupts a screen reader; the rest are read when reached.
    <p role={tone === 'bad' ? 'alert' : 'status'} className={className}>
      {children}
    </p>
  );
}

/**
 * The page's name, and, behind a mark, what the page is for.
 *
 * The sentence under a heading is written for the first visit and read on that
 * visit only; after it, it is a line of grey the eye steps over on every load,
 * pushing the actual work down the screen. It stays available to whoever wants
 * it: a mark beside the title opens it, and closes it again.
 */
/** One section of a screen: what the tab says, and how the screen is told. */
export interface PageTab {
  key: string;
  /* A node, not a string: a section whose name carries its own mark — the
     service dot on a group of keys — is still one tab, not a second row. */
  label: React.ReactNode;
  /** A count worth seeing before the tab is opened. */
  count?: number;
  /** Set for a section that lives at its own address. */
  href?: string;
}

export function PageHead({
  title,
  count,
  aside,
  tabs,
  active,
  onSelect,
  toolbar,
  alerts,
  children,
}: {
  title: string;
  /**
   * A page-level action — an "Add" button, mostly — pushed to the far right
   * of the heading row regardless of whether the info mark beside the title
   * is present. Kept apart from `children`: that sentence is read once and
   * put away, this is a control the reader comes back to on every visit.
   */
  /* How many things this screen holds, beside its name. `null` while the
     number is still coming: the chip keeps its place rather than appearing
     under the reader's eye a second after the page draws. */
  count?: number | null;
  aside?: React.ReactNode;
  /* The screen's own sections: a second band under the title, closed by its
     own line — where you are, then what you are looking at. */
  tabs?: readonly PageTab[];
  active?: string;
  onSelect?: (key: string) => void;
  /* The controls that decide what the screen shows: a third band, same white,
     same closing line. Filters, a sort, whichever switch this screen has. */
  toolbar?: React.ReactNode;
  /* What is asking for a person right now, if the screen counts such a thing:
     a fourth band. Not a filter over the work — the reason to open it. */
  alerts?: React.ReactNode;
  children?: React.ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const anchor = useRef<HTMLSpanElement>(null);
  const noteId = useId();
  const label = open ? 'Hide what this page is for' : 'What is this page for?';

  // Escape, and a click anywhere else. Both were missing: the only way to put
  // the sentence away was to find the mark again, which is the one thing a
  // reader who has finished reading is not looking at.
  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    const onPointerDown = (event: MouseEvent) => {
      if (!anchor.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onPointerDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onPointerDown);
    };
  }, [open]);

  return (
    <header className="page-head">
      <div className="page-head-row">
        <h1>{title}</h1>
        {count !== undefined && (
          /* `null`: counts, number not here yet — the chip keeps its place.
             `undefined`: nothing to count, and nothing is drawn. */
          <span
            className={`page-head-count figures${count === null ? ' is-waiting' : ''}`}
            aria-label={count === null ? undefined : `${count} in total`}
            aria-hidden={count === null ? true : undefined}
          >
            {count === null ? '—' : count.toLocaleString()}
          </span>
        )}
        {/* The mark and its note, and nothing else. Anchored on the whole
            heading row, the empty half of a full-width flex row counted as
            "inside" and swallowed the click that was meant to put the note
            away. */}
        {children && (
          <span className="page-head-ask" ref={anchor}>
            <button
              type="button"
              className="page-head-info"
              onClick={() => setOpen((shown) => !shown)}
              aria-expanded={open}
              aria-controls={noteId}
              aria-label={label}
            >
              <svg width="14" height="14" viewBox="0 0 16 16" aria-hidden>
                <circle cx="8" cy="8" r="6.85" fill="none" stroke="currentColor" strokeWidth="1.1" />
                <path
                  d="M8 7.4v3.6"
                  stroke="currentColor"
                  strokeWidth="1.25"
                  strokeLinecap="round"
                />
                <circle cx="8" cy="5" r="0.72" fill="currentColor" />
              </svg>
            </button>
            {/* Over the page, not inserted into it. As a paragraph in the flow
                it pushed every row of the table down the moment it was opened
                and pulled them back up on close, so the thing the reader had
                come to look at moved under the cursor twice for one sentence.
                Rendered whether open or not, and hidden with an attribute, so
                a screen reader following aria-controls has something to
                find. */}
            {/* A div, not a p: pages hand this a checklist, and a <ul> inside
                a <p> is closed by the parser before it — the browser's DOM
                then differs from the one React rendered on the server, which
                is a hydration failure on every load of that page. */}
            <div id={noteId} className="page-head-note" hidden={!open} role="note">
              {children}
            </div>
          </span>
        )}
        {aside && <span className="page-head-aside">{aside}</span>}
      </div>
      {tabs && tabs.length > 0 && (
        <div className="page-tabs" role="tablist" aria-label={`${title} sections`}>
          {tabs.map((tab) => {
            const on = tab.key === active;
            const inside = (
              <>
                {tab.label}
                {typeof tab.count === 'number' && <span className="page-tab-n">{tab.count}</span>}
              </>
            );
            /* A section at its own address is a link, so it opens in a new tab
               like any other and the browser's own "back" knows about it. One
               that lives on this screen is a button, because nothing navigates. */
            return tab.href ? (
              <a
                key={tab.key}
                href={tab.href}
                role="tab"
                aria-selected={on}
                className={`page-tab${on ? ' is-on' : ''}`}
              >
                {inside}
              </a>
            ) : (
              <button
                key={tab.key}
                type="button"
                role="tab"
                aria-selected={on}
                className={`page-tab${on ? ' is-on' : ''}`}
                onClick={() => onSelect?.(tab.key)}
              >
                {inside}
              </button>
            );
          })}
        </div>
      )}
      {toolbar && <div className="page-toolbar">{toolbar}</div>}
      {alerts && <div className="page-alerts">{alerts}</div>}
    </header>
  );
}

/**
 * The name and where you are. Left in the rail, above a column of links;
 * centred on the sign-in card, which has no left edge to line up with.
 *
 * `name` has no default on purpose. The support app guards its pages in the
 * browser, so /queue answers an unauthenticated request with the shell around
 * the guard, and a default here would put the product name in that markup and
 * in readable JavaScript — exactly what the sign-in page takes care not to do.
 */
export function Wordmark({
  context,
  name,
  align = 'start',
}: {
  context: string;
  name: string | null;
  align?: 'start' | 'center';
}) {
  return (
    <div className={`titlemark titlemark-${align}`}>
      {name && <strong className="titlemark-name">{name}</strong>}
      <span className={name ? 'titlemark-context' : 'titlemark-solo'}>{context}</span>
    </div>
  );
}

export function EmptyState({ title, children }: { title: string; children?: React.ReactNode }) {
  return (
    <div className="empty">
      <strong>{title}</strong>
      {children}
    </div>
  );
}

/**
 * Where a detection came from, in words rather than in pipeline codes.
 *
 * Every value SourceType can hold is named here. An unmapped one used to fall
 * through as the raw code, so a page that had been working for months started
 * showing "forum" in lower case the day that connector was switched on.
 */
export function sourceLabel(source: string): string {
  const labels: Record<string, string> = {
    search: 'Search monitor',
    social: 'Social monitor',
    tube: 'Video site',
    filehost: 'File host',
    imagehost: 'Image host',
    forum: 'Forum or blog',
    torrent: 'Torrent site',
    shortener: 'Shortened link',
    messenger: 'Messenger',
    crawler: 'Crawler',
    manual: 'You submitted it',
  };
  // Anything new gets a readable shape rather than a lower-case code.
  return labels[source] ?? source.charAt(0).toUpperCase() + source.slice(1);
}

const SOURCE_ICON_PROPS = { width: 15, height: 15, viewBox: '0 0 16 16', 'aria-hidden': true } as const;
const SOURCE_STROKE = { fill: 'none', stroke: 'currentColor', strokeWidth: 1.4 } as const;

/** A clock: nothing has looked yet, which is a different fact than nothing to report. */
function PendingIcon() {
  return (
    <svg {...SOURCE_ICON_PROPS}>
      <circle cx="8" cy="8" r="5.5" {...SOURCE_STROKE} />
      <path d="M8 5v3.3l2.2 1.3" {...SOURCE_STROKE} strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/** A checked box: the same mark /plan's own "Whitelist your own pages" row
 * uses, so a whitelisted finding and the entitlement that made it possible
 * read as the same fact wherever either appears. */
export function WhitelistedIcon() {
  return (
    <svg {...SOURCE_ICON_PROPS}>
      <rect x="2.5" y="2.5" width="11" height="11" rx="2.5" {...SOURCE_STROKE} />
      <path
        d="M5.3 8.3l1.8 1.8 3.6-3.8"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/** A magnifier: search engines, read the plain way. */
export function SearchIcon() {
  return (
    <svg {...SOURCE_ICON_PROPS}>
      <circle cx="7" cy="7" r="4.5" {...SOURCE_STROKE} />
      <path d="M10.3 10.3L14 14" {...SOURCE_STROKE} strokeLinecap="round" />
    </svg>
  );
}

/** Three linked points: a platform is a network, not a page. */
export function SocialIcon() {
  return (
    <svg {...SOURCE_ICON_PROPS}>
      <circle cx="4.5" cy="5" r="1.8" {...SOURCE_STROKE} />
      <circle cx="12" cy="4.5" r="1.8" {...SOURCE_STROKE} />
      <circle cx="8" cy="12" r="1.8" {...SOURCE_STROKE} />
      <path d="M5.9 6.1L7 10.6M10.6 5.6L9 10.4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  );
}

/** A play mark: leak and tube sites host video. */
export function TubeIcon() {
  return (
    <svg {...SOURCE_ICON_PROPS}>
      <circle cx="8" cy="8" r="6.2" {...SOURCE_STROKE} />
      <path d="M6.6 5.6l4 2.4-4 2.4z" fill="currentColor" stroke="currentColor" strokeWidth="1" strokeLinejoin="round" />
    </svg>
  );
}

/** A folder: file hosts store what got uploaded, not what got posted. */
export function FileHostIcon() {
  return (
    <svg {...SOURCE_ICON_PROPS}>
      <path d="M2 4.5h4l1.3 1.6H14v6.4a1 1 0 01-1 1H2z" {...SOURCE_STROKE} strokeLinejoin="round" />
    </svg>
  );
}

/** A framed landscape: an image host is a gallery, not a filing cabinet. */
export function ImageHostIcon() {
  return (
    <svg {...SOURCE_ICON_PROPS}>
      <rect x="2" y="3" width="12" height="10" rx="1" {...SOURCE_STROKE} />
      <circle cx="5.6" cy="6.3" r="1.1" fill="currentColor" stroke="none" />
      <path d="M3 11.5l3.4-3.4 2 2L11.5 7 13.5 9.5" {...SOURCE_STROKE} strokeLinejoin="round" />
    </svg>
  );
}

/** A magnet: what a torrent actually is, two poles and a swarm between them. */
export function TorrentIcon() {
  return (
    <svg {...SOURCE_ICON_PROPS}>
      <path d="M4.5 9V5.5a3.5 3.5 0 017 0V9" {...SOURCE_STROKE} strokeLinecap="round" />
      <path d="M2.5 9h4v3.5a2 2 0 01-4 0zM9.5 9h4v3.5a2 2 0 01-4 0z" {...SOURCE_STROKE} strokeLinejoin="round" />
    </svg>
  );
}

/** A speech bubble: forums are conversation, which is where doxxing spreads. */
export function ForumIcon() {
  return (
    <svg {...SOURCE_ICON_PROPS}>
      <path d="M2.5 3.5h11v7h-6L4.5 13v-2.5h-2z" {...SOURCE_STROKE} strokeLinejoin="round" />
    </svg>
  );
}

/** A broken chain: a shortener is a link standing in for another address. */
export function ShortenerIcon() {
  return (
    <svg {...SOURCE_ICON_PROPS}>
      <path d="M6.5 9.5l3-3" {...SOURCE_STROKE} strokeLinecap="round" />
      <path d="M7 4.5L8.3 3.2a2.3 2.3 0 013.3 3.3L10.3 7.8" {...SOURCE_STROKE} strokeLinecap="round" />
      <path d="M9 11.5L7.7 12.8a2.3 2.3 0 01-3.3-3.3L5.7 8.2" {...SOURCE_STROKE} strokeLinecap="round" />
    </svg>
  );
}

/** A paper plane: sent, not posted, which is why nobody else can search it for us. */
export function MessengerIcon() {
  return (
    <svg {...SOURCE_ICON_PROPS}>
      <path d="M2 8.5l12-6-4 12-3-4.5z" {...SOURCE_STROKE} strokeLinejoin="round" />
      <path d="M10 2.5L5.5 10" {...SOURCE_STROKE} strokeLinecap="round" />
    </svg>
  );
}

/** A globe with a meridian: a crawler walks the open web, not one named place. */
export function CrawlerIcon() {
  return (
    <svg {...SOURCE_ICON_PROPS}>
      <circle cx="8" cy="8" r="5.5" {...SOURCE_STROKE} />
      <path d="M2.5 8h11M8 2.5c1.8 1.6 1.8 9.4 0 11M8 2.5c-1.8 1.6-1.8 9.4 0 11" {...SOURCE_STROKE} />
    </svg>
  );
}

/** A flag planted by hand: this one did not turn up in a sweep, she marked it herself. */
export function ManualIcon() {
  return (
    <svg {...SOURCE_ICON_PROPS}>
      <path d="M4 13.5V2.5" {...SOURCE_STROKE} strokeLinecap="round" />
      <path d="M4 3l8 2.5-8 2.5z" fill="currentColor" stroke="currentColor" strokeWidth="1" strokeLinejoin="round" />
    </svg>
  );
}

/** A shield: the same mark the plan cards use for this exact capability. */
export function ShieldIcon() {
  return (
    <svg {...SOURCE_ICON_PROPS}>
      <path d="M8 2l5 2v4c0 3-2 5-5 6-3-1-5-3-5-6V4z" {...SOURCE_STROKE} strokeLinejoin="round" />
    </svg>
  );
}

/** The icon for where a detection came from, paired one-to-one with {@link sourceLabel}. */
export function sourceIcon(source: string) {
  const icons: Record<string, () => ReturnType<typeof SearchIcon>> = {
    search: SearchIcon,
    social: SocialIcon,
    tube: TubeIcon,
    filehost: FileHostIcon,
    imagehost: ImageHostIcon,
    forum: ForumIcon,
    torrent: TorrentIcon,
    shortener: ShortenerIcon,
    messenger: MessengerIcon,
    crawler: CrawlerIcon,
    manual: ManualIcon,
  };
  const Icon = icons[source] ?? CrawlerIcon;
  return <Icon />;
}
